import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { store } from '@/store';
import { USER_POOL } from './authToken.thunk.ts';
import { setRefreshedSession, setSessionExpiration } from './authToken.slice.ts';
import { getAccountFromIdToken } from '@/utilities';
import {
  getJwtExpiration,
  isTokenExpired,
  TOKEN_EXPIRY_SKEW_SECONDS,
} from '@/utilities/getTokenExpiration.ts';
import { IUserPortal } from 'interfaces/UserAccountInfo.interface.ts';
import { Nullable } from 'interfaces/Nullable.ts';

/**
 * Session lifetime for the Palmetto access token.
 *
 *   login (Cognito SRP)  ─┐
 *   portal ?id_token=... ─┴─▶ ID token ──▶ custom:chunkN ──▶ account.accessToken
 *                                │
 *                                └── exp ──▶ authToken.tokenExpiresAt
 *
 * The Palmetto access token is opaque, so the ID token's `exp` is the only
 * client-visible expiry. Users who signed in through Cognito have a refresh
 * token in the pool's local storage and can be renewed silently; users who
 * arrived from the portal with `?id_token=` have no refresh token and can only
 * be told to sign in again.
 */

type RefreshedSession = {
  token: string;
  account: IUserPortal;
  expiresAt: Nullable<number>;
};

/** One shared refresh across concurrent callers, so a burst of parallel
 *  requests triggers a single token exchange instead of one per request. */
let inFlightRefresh: Nullable<Promise<Nullable<RefreshedSession>>> = null;

function getCognitoSession(): Promise<Nullable<CognitoUserSession>> {
  return new Promise((resolve) => {
    const user = USER_POOL.getCurrentUser();
    if (!user) return resolve(null);
    // getSession() reads the persisted session and, when the ID/access tokens
    // have expired, exchanges the refresh token for new ones before returning.
    user.getSession((error: Error | null, session: Nullable<CognitoUserSession>) => {
      if (error) {
        console.warn('Could not restore Cognito session', error);
        return resolve(null);
      }
      resolve(session ?? null);
    });
  });
}

function readSession(session: CognitoUserSession): Nullable<RefreshedSession> {
  const idToken = session.getIdToken();
  try {
    const account = JSON.parse(getAccountFromIdToken(idToken) as string) as IUserPortal;
    if (!account?.accessToken) return null;
    return {
      token: account.accessToken,
      account,
      expiresAt: idToken.getExpiration(),
    };
  } catch (error) {
    // The account is LZ-compressed across custom:chunkN claims. A claim that
    // fails to decompress or parse leaves us with no usable token, so report it
    // as "no session" rather than throwing out of the refresh path.
    console.warn('Could not read account from ID token', error);
    return null;
  }
}

/**
 * Records when the current session expires. Called once when the session
 * becomes active. `idTokenFromUrl` covers the portal hand-off, where no Cognito
 * session exists locally and the raw JWT is the only source of `exp`.
 */
export async function syncSessionExpiration(idTokenFromUrl?: Nullable<string>): Promise<void> {
  const session = await getCognitoSession();
  const expiresAt = session
    ? session.getIdToken().getExpiration()
    : getJwtExpiration(idTokenFromUrl ?? null);
  store.dispatch(setSessionExpiration(expiresAt ?? null));
}

/** Renews the session through the Cognito refresh token. Returns null when no
 *  refresh is possible (portal hand-off, offline, revoked refresh token). */
export async function refreshSession(): Promise<Nullable<RefreshedSession>> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    const session = await getCognitoSession();
    if (!session || !session.isValid()) return null;
    const refreshed = readSession(session);
    if (!refreshed) return null;
    store.dispatch(
      setRefreshedSession({
        token: refreshed.token,
        tokenVerification: refreshed.account,
        expiresAt: refreshed.expiresAt,
      })
    );
    return refreshed;
  })()
    .catch((error) => {
      console.warn('Session refresh failed', error);
      return null;
    })
    .finally(() => {
      inFlightRefresh = null;
    });

  return inFlightRefresh;
}

/**
 * Called before every authenticated request. Renews the token when it is
 * inside the expiry skew window and returns the token to send. Falls back to
 * the token already in the store whenever a refresh is impossible, so an
 * offline device keeps working exactly as it does today.
 */
export async function ensureFreshSession(): Promise<Nullable<string>> {
  const { token, tokenExpiresAt } = store.getState().authToken;
  if (!isTokenExpired(tokenExpiresAt, TOKEN_EXPIRY_SKEW_SECONDS)) return token;

  const refreshed = await refreshSession();
  return refreshed?.token ?? token;
}

/**
 * True when the session is beyond saving. A 401 is only allowed to end the
 * session when this returns true.
 */
export async function isSessionUnrecoverable(): Promise<boolean> {
  const state = store.getState();
  const { token, tokenExpiresAt } = state.authToken;
  if (!token) return true;
  // Offline devices can be handed a 401 by a captive portal. Losing the session
  // there would strand a field user with an unsynced queue and no way back in.
  if (!state.connection.health) return false;
  // A token still inside its recorded lifetime is provably alive, so the 401 is
  // an authorization denial rather than a dead session.
  if (tokenExpiresAt && !isTokenExpired(tokenExpiresAt)) return false;
  // Expired, or expiry unknown (a session carried over from a build before it
  // was recorded): renewable means alive, otherwise the session is over.
  const refreshed = await refreshSession();
  return !refreshed;
}
